// 非編集時のテキスト / 付箋本文を描画するコンポーネント。本文中の URL を自動でハイパーリンク化する。
//
// クリック挙動（要件④）:
// - 確定後（非編集）の単一クリック: 少し遅延してから開く。遅延中にダブルクリック（＝編集開始）が
//   来たら開くのをキャンセルし、コンテナ側の onDoubleClick（編集モード遷移）へ譲る。
// - 修飾キー（Ctrl/Cmd/Shift/Alt）付きクリックや中クリックは、<a> の標準挙動に委ねる。
// - 参照専用（埋め込み）は編集が無いため、単一クリックで即開く。
// リンクは data-canvas-link="true" を持ち、use-object-drag はこの上での pointerdown で選択/ドラッグを
// 開始しない（クリックがリンク遷移として成立する）。
//
// リンク表示仕様:
// - 外部リンク（オリジンが異なる）: 新規タブ + rel="noopener noreferrer" + 末尾に
//   「新しいタブを開きます」アイコン（クリック可能領域はアイコンまで含む）
// - 内部リンク（同一オリジン）: 同一タブ遷移・アイコンなし
// - 表示テキストが URL そのもののリンクは 1 行省略表示（…）とし、title でフル URL を確認できる
// - 表示テキスト付きリンク（[テキスト](URL)）は表示テキストのままリンク化する

import { useEffect, useRef, type ReactNode } from "react";
import { isExternalHttpUrl, splitTextIntoLinkSegments } from "../../../lib/linkify-text";
import { useCanvasStore } from "../store/canvas-store";

// 単一クリックで開くまでの遅延。この間にダブルクリックが来たら編集へ切り替えるためキャンセルする。
const OPEN_DELAY_MS = 250;

function openUrl(url: string, external: boolean, readOnly: boolean): void {
  // 外部リンク、または参照専用（埋め込み iframe）表示のときは新規タブで開く。
  // 埋め込み時に内部リンクを同一タブ遷移させると iframe の内容が置き換わり、
  // 閲覧者が埋め込みから抜け出せなくなるため、readOnly では内部リンクも新規タブにする。
  if (external || readOnly) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  // 通常表示の内部リンク（同一オリジン）は同一タブで遷移する。
  window.location.assign(url);
}

/**
 * 外部リンク末尾のアイコン（別サイトへ別タブで遷移することを示す）。
 * 装飾ではなく意味を持つため、支援技術向けの代替テキストを付与する。
 * コメント本文のリンク描画（LinkifiedCommentText）でも同じ見た目を共有するため export する。
 */
export function ExternalLinkIcon(): ReactNode {
  return (
    <svg
      aria-label="新しいタブを開きます"
      className="external-link-icon"
      fill="none"
      role="img"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.2}
      viewBox="0 0 24 24"
    >
      <path d="M7 17 17 7M7 7h10v10" />
    </svg>
  );
}

export function LinkifiedText({ text }: { readonly text: string }): JSX.Element {
  const readOnly = useCanvasStore((s) => s.readOnly);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    };
  }, []);

  const segments = splitTextIntoLinkSegments(text);

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "text" || !segment.url) {
          // key はセグメント列のインデックス。text は再レンダーのたびに再分割されるため安定。
          return <span key={index}>{segment.value}</span>;
        }
        const url = segment.url;
        const external = isExternalHttpUrl(url);
        // 表示テキストが URL そのもの（自動リンク化）の場合のみ 1 行省略表示の対象にする。
        const bareUrl = segment.value === url;
        const className = [
          "canvas-linkified-link",
          ...(bareUrl ? ["canvas-linkified-link--url"] : []),
        ].join(" ");
        return (
          <a
            key={index}
            className={className}
            href={url}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            draggable={false}
            data-canvas-link="true"
            title={bareUrl ? url : undefined}
            onClick={(event) => {
              // 修飾キー付きクリックはブラウザ標準（新規タブ / バックグラウンドタブ）に委ねる。
              if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              if (readOnly) {
                openUrl(url, external, readOnly);
                return;
              }
              // ダブルクリックの 2 回目（detail>=2）なら開くのをやめ、編集モード遷移へ譲る。
              if (event.detail >= 2) {
                if (pendingTimerRef.current) {
                  clearTimeout(pendingTimerRef.current);
                  pendingTimerRef.current = null;
                }
                return;
              }
              // 単一クリック: 少し遅延して開く（この間にダブルクリックが来たら上でキャンセルされる）。
              if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
              pendingTimerRef.current = setTimeout(() => {
                pendingTimerRef.current = null;
                openUrl(url, external, readOnly);
              }, OPEN_DELAY_MS);
            }}
          >
            {bareUrl ? (
              <span className="canvas-linkified-link-label">{segment.value}</span>
            ) : (
              segment.value
            )}
            {external ? <ExternalLinkIcon /> : null}
          </a>
        );
      })}
    </>
  );
}
